import {
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type KeyboardEvent,
    type ReactElement,
} from "react";
import { Search } from "lucide-react";
import { Text, TextInput } from "@ux/primitives";
import { palette, radius, space, typography } from "@ux/tokens";
import { useDebouncedValue } from "../../admin/common/useDebouncedValue";
import { formatUserDisplay, formatUserShortName } from "../../admin/users/display";
import type { UserDirectoryEntry } from "../../admin/users/types";
import {
    floorplanMapElementId,
    floorplanSpaceTitle,
    workspaceKind,
    type FloorplanSpaceView,
} from "../../admin/workspaces/types";

/** Wait for typing to pause before filtering / dimming the map. */
const SEARCH_DEBOUNCE_MS = 350;

export interface FloorplanSearchProps {
    readonly spaces: readonly FloorplanSpaceView[];
    readonly query: string;
    readonly onQueryChange: (q: string) => void;
    readonly onSelectResult: (elementId: string) => void;
    readonly onClear: () => void;
    readonly focusNonce?: number;
    /** Optional pre-resolved HR names (avoids a second directory lookup). */
    readonly userIndex?: ReadonlyMap<string, UserDirectoryEntry | null>;
}

interface SearchHit {
    readonly elementId: string;
    readonly title: string;
    readonly meta: string;
}

export function FloorplanSearch({
    spaces,
    query,
    onQueryChange,
    onSelectResult,
    onClear,
    focusNonce = 0,
    userIndex,
}: FloorplanSearchProps): ReactElement {
    const listId = useId();
    const wrapRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    // Immediate text in the field; committed search waits for debounce.
    const [draft, setDraft] = useState(query);
    const debouncedDraft = useDebouncedValue(draft, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        setDraft(query);
    }, [query]);

    useEffect(() => {
        if (debouncedDraft === query) return;
        onQueryChange(debouncedDraft);
    }, [debouncedDraft, query, onQueryChange]);

    const resolvedUsers = userIndex ?? EMPTY_USER_INDEX;

    const matches = useMemo((): SearchHit[] => {
        const q = debouncedDraft.trim().toLowerCase();
        if (!q) return [];
        const hits: SearchHit[] = [];
        for (const space of spaces) {
            const ws = space.workspace;
            const elementId = floorplanMapElementId(ws) ?? "";
            if (!elementId) continue;
            const occupant =
                space.occupantUserId !== null && space.occupantUserId !== undefined
                    ? (resolvedUsers.get(space.occupantUserId) ?? null)
                    : null;
            const occupantLabel =
                space.occupantUserId !== null && space.occupantUserId !== undefined
                    ? formatUserDisplay(occupant ?? null, space.occupantUserId)
                    : "";
            const assignedName = formatUserShortName(occupant);
            const hay = [
                elementId,
                ws.name,
                ws.friendlyName ?? "",
                ws.type,
                occupantLabel,
                assignedName ?? "",
                occupant?.email ?? "",
                occupant?.userPrincipalName ?? "",
            ]
                .join(" ")
                .toLowerCase();
            if (!hay.includes(q)) continue;
            const kind = workspaceKind(ws);
            const occupied =
                space.occupantUserId !== null &&
                space.occupantUserId !== undefined &&
                space.occupantUserId !== "";
            hits.push({
                elementId,
                title: floorplanSpaceTitle(
                    assignedName,
                    ws.friendlyName,
                    ws.name?.trim() || elementId,
                ),
                meta: occupied
                    ? `${kind} · occupied`
                    : `${elementId} · ${kind}${kind === "desk" ? " · vacant" : ""}`,
            });
            if (hits.length >= 12) break;
        }
        return hits;
    }, [spaces, debouncedDraft, resolvedUsers]);

    useEffect(() => {
        setActiveIndex(0);
        setOpen(draft.trim().length > 0);
    }, [draft, matches.length]);

    useEffect(() => {
        if (focusNonce <= 0) return;
        const input = wrapRef.current?.querySelector("input");
        input?.focus();
        input?.select();
    }, [focusNonce]);

    useEffect(() => {
        function onDocClick(e: MouseEvent): void {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    function choose(index: number): void {
        const hit = matches[index];
        if (!hit) return;
        onSelectResult(hit.elementId);
        setDraft(hit.title);
        onQueryChange(hit.title);
        setOpen(false);
    }

    function clearNow(): void {
        setDraft("");
        onClear();
        setOpen(false);
    }

    function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
        if (e.key === "Escape") {
            e.preventDefault();
            if (draft) clearNow();
            else setOpen(false);
            return;
        }
        if (e.key === "Tab" && open && matches.length > 0) {
            e.preventDefault();
            const dir = e.shiftKey ? -1 : 1;
            setActiveIndex((i) => (i + dir + matches.length) % matches.length);
            return;
        }
        if (e.key === "ArrowDown" && matches.length > 0) {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((i) => (i + 1) % matches.length);
            return;
        }
        if (e.key === "ArrowUp" && matches.length > 0) {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
            return;
        }
        if (e.key === "Enter" && open && matches.length > 0) {
            e.preventDefault();
            choose(activeIndex);
        }
    }

    const waitingForDebounce =
        draft.trim() !== debouncedDraft.trim() && draft.trim().length > 0;

    return (
        <div ref={wrapRef} style={wrapStyle}>
            <div style={inputRowStyle}>
                <Search size={16} color={palette.fgSubtle} aria-hidden />
                <TextInput
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onFocus={() => {
                        if (matches.length) setOpen(true);
                    }}
                    onKeyDown={onKeyDown}
                    placeholder="Search name or desk ID…  (/)"
                    aria-autocomplete="list"
                    aria-controls={listId}
                    aria-expanded={open}
                    autoComplete="off"
                />
            </div>
            {open ? (
                <ul id={listId} role="listbox" style={listStyle}>
                    {waitingForDebounce ? (
                        <li style={emptyStyle}>
                            <Text role="muted">Searching…</Text>
                        </li>
                    ) : matches.length === 0 ? (
                        <li style={emptyStyle}>
                            <Text role="muted">No matches</Text>
                        </li>
                    ) : (
                        matches.map((hit, i) => {
                            const active = i === activeIndex;
                            return (
                                <li
                                    key={hit.elementId}
                                    role="option"
                                    aria-selected={active}
                                    style={{
                                        ...itemStyle,
                                        background: active
                                            ? palette.accentBg
                                            : "transparent",
                                    }}
                                    onMouseEnter={() => setActiveIndex(i)}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        choose(i);
                                    }}
                                >
                                    <span style={nameStyle}>{hit.title}</span>
                                    <span style={metaStyle}>{hit.meta}</span>
                                </li>
                            );
                        })
                    )}
                </ul>
            ) : null}
        </div>
    );
}

const EMPTY_USER_INDEX: ReadonlyMap<string, UserDirectoryEntry | null> = new Map();

const wrapStyle: CSSProperties = {
    position: "relative",
    minWidth: 260,
    width: "min(360px, 100%)",
};

const inputRowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: space[2],
};

const listStyle: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    margin: 0,
    padding: space[1],
    listStyle: "none",
    background: "#15171c",
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: radius.md,
    maxHeight: 280,
    overflowY: "auto",
    zIndex: 30,
    boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
};

const itemStyle: CSSProperties = {
    padding: `${space[2]} ${space[3]}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 2,
};

const emptyStyle: CSSProperties = {
    padding: `${space[2]} ${space[3]}`,
};

const nameStyle: CSSProperties = {
    fontFamily: typography.family.body,
    fontSize: typography.size.sm,
    color: palette.fg,
    fontWeight: typography.weight.semibold,
};

const metaStyle: CSSProperties = {
    fontFamily: typography.family.body,
    fontSize: typography.size.xs,
    color: palette.fgMuted,
};
